import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { Cluster } from '../types'
import { Article } from '../../../perspectives/perspective/types'

export interface ClusterCardsRef {
  moveTo: (index: number) => void
}
import { Platform } from 'react-native'
import { SwipeableCards } from '../../../../../@ui'
import { useWechatShareInit } from '../../../../../hooks/wechat-share.hook'

export const ClusterCards = forwardRef<
  ClusterCardsRef,
  {
    clusters: Cluster[]
    nodeId: string
    selectedClusterIndex: number
    renderCard: (cluster: Cluster, index: number) => React.ReactNode
    setSelectedClusterIndex: (index: number) => void
  }
>(
  (
    {
      clusters,
      nodeId,
      selectedClusterIndex,
      setSelectedClusterIndex,
      renderCard,
    },
    ref
  ) => {
    // Is a function provide for the swipper to move to a card programmatically
    const moveToCard = useRef<{ (card: number): void } | undefined>(undefined)

    useImperativeHandle(ref, () => ({
      moveTo: (index: number) => {
        moveToCard.current?.(index)
      },
    }))

    /**
     * USE EFFECTS
     */
    useEffect(() => {
      if (Platform.OS !== 'web') {
        return
      }
      if (clusters.length === 0) {
        return
      }
      const clusterId = clusters[selectedClusterIndex]?.id
      if (!clusterId) {
        return
      }

      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('clusterId', clusterId)
      searchParams.set('nodeId', nodeId)
      window.history.replaceState(null, '', `?${searchParams.toString()}`)
    }, [selectedClusterIndex])

    useWechatShareInit(() => {
      if (!clusters[selectedClusterIndex]) return null

      const currentCluster = clusters[selectedClusterIndex]
      const perspectiveCount = currentCluster.articlesCount
      const voteCount = currentCluster.votesCount

      const hasPerspectives = perspectiveCount > 0
      const hasVotes = voteCount > 0

      if (!hasPerspectives || !hasVotes) {
        return {
          title: currentCluster.topic,
        }
      }

      return {
        title: currentCluster.topic,
        description: `${perspectiveCount} perspective(s) and ${voteCount} vote(s)`,
      }
    }, [clusters, selectedClusterIndex])

    /**
     * USE EFFECTS
     */
    // If the data changes (some cluster updated), make sure the user keeps seeing the right cluster when they re-order
    useEffect(() => {
      if (clusters.length === 0) {
        return
      }
      if (selectedClusterIndex < 0) {
        return
      }
      moveToCard.current?.(selectedClusterIndex)
    }, [clusters, selectedClusterIndex])

    return (
      <SwipeableCards
        initialSelectedIndex={selectedClusterIndex}
        onSelectedIxChange={(index) => {
          setSelectedClusterIndex(index)
        }}
        moveToCardAction={(action) => (moveToCard.current = action)}
      >
        {clusters.map((cluster, index) => renderCard(cluster, index))}
      </SwipeableCards>
    )
  }
)
